// src/Library.tsx
// The asset library: upload images, music and video; browse and filter
// them; edit each asset's roles, tags, book and description. Descriptions
// are written later by the worker (descStatus pending → done), so while any
// asset is still pending this page polls for just the rows that changed.
// Each upload batch is queued for description as one job. While an asset is
// queued its description fields are read-only, unless the user chooses
// "Write it myself" (which takes it out of the queue).
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isActiveMember } from '@ring/shared/author-trust';
import {
  IMAGE_ROLES, MAX_DESCRIBE_ASSETS, isDescQueued, isDescribable,
  type AssetColors, type AssetKind, type ImageRole, type DescStatus,
} from '@ring/shared/assets';
import { DEFAULT_STORAGE_QUOTA_MB } from '@ring/shared/author-trust';
import {
  listAssets, uploadAsset, uploadThumbnail, updateAsset, deleteAsset, describeAssets, cancelDescription, type AssetSummary,
} from './assetsApi';
import { probeFile } from './mediaProbe';
import { BookSelect } from './BookSelect';
import './Library.css';

const POLL_MS = 20_000;
/**
 * Only recently-touched assets keep the page polling. An asset still
 * 'pending' after this long isn't about to change (no worker running, or AI
 * off for the account), so polling for it forever would just cost requests.
 */
const POLL_WINDOW_MS = 30 * 60 * 1000;
const UPLOAD_CONCURRENCY = 2;

const KIND_LABEL: Record<AssetKind, string> = { image: 'Image', music: 'Music', video: 'Video' };

const DESC_LABEL: Record<DescStatus, string> = {
  pending: 'Queued for AI to describe',
  running: 'AI is describing…',
  done:    'Described',
  failed:  'Description failed',
  skipped: 'No description',
};

interface UploadItem {
  id:       string;
  file:     File;
  state:    'queued' | 'reading' | 'uploading' | 'done' | 'error';
  progress: number;
  error?:   string;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatDuration(s: number | null): string | null {
  if (s === null) return null;
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, '0')}`;
}

/** e.g. "dark · cool · muted · low contrast", from the worker's measurements. */
function describeColors(c: AssetColors): string {
  const light = c.brightness < 35 ? 'dark' : c.brightness > 65 ? 'bright' : 'mid-tone';
  const vivid = c.colorfulness < 15 ? 'muted' : c.colorfulness > 80 ? 'vivid' : null;
  const contrast = c.contrast < 15 ? 'low contrast' : c.contrast > 30 ? 'high contrast' : null;
  return [light, c.temperature === 'neutral' ? null : c.temperature, vivid, contrast].filter(Boolean).join(' · ');
}

function matchesSearch(a: AssetSummary, q: string): boolean {
  if (!q) return true;
  const hay = [a.filename, a.descShort, a.descLong, a.mood, a.bookCode, ...a.tags, ...a.roles]
    .filter(Boolean).join(' ').toLowerCase();
  return q.toLowerCase().split(/\s+/).every(term => hay.includes(term));
}

export function Library() {
  const { author, loading: authLoading, refreshSession } = useAuth();
  const navigate = useNavigate();

  const [assets, setAssets]       = useState<Map<string, AssetSummary>>(new Map());
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [uploads, setUploads]     = useState<UploadItem[]>([]);
  const [kindFilter, setKindFilter] = useState<AssetKind | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<ImageRole | 'any'>('any');
  const [search, setSearch]       = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragging, setDragging]   = useState(false);
  const serverTime = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const active = author ? isActiveMember(author.authorStatus) : false;

  useEffect(() => {
    if (!authLoading && (!author || !active)) navigate('/dashboard');
  }, [authLoading, author, active, navigate]);

  // Account settings (AI on/off) are read when the site loads; an admin may have changed them since
  useEffect(() => {
    refreshSession().catch(() => { /* keep what's cached; the next page load tries again */ });
  }, [refreshSession]);

  const mergeAssets = useCallback((incoming: AssetSummary[]) => {
    setAssets(prev => {
      const next = new Map(prev);
      for (const a of incoming) next.set(a.assetId, a);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    listAssets()
      .then(list => {
        setAssets(new Map(list.assets.map(a => [a.assetId, a])));
        serverTime.current = list.serverTime;
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load your library.'))
      .finally(() => setLoading(false));
  }, [active]);

  // Poll for description progress, but only while something is still waiting and the tab is visible.
  const anyInProgress = useMemo(() => {
    const cutoff = Date.now() - POLL_WINDOW_MS;
    return [...assets.values()].some(a =>
      (a.descStatus === 'pending' || a.descStatus === 'running' || a.measureStatus === 'pending') && Date.parse(a.updatedAt) > cutoff);
  }, [assets]);
  useEffect(() => {
    if (!anyInProgress) return;
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible' || !serverTime.current) return;
      listAssets(serverTime.current)
        .then(list => { mergeAssets(list.assets); serverTime.current = list.serverTime; })
        .catch(() => { /* transient — next tick retries */ });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [anyInProgress, mergeAssets]);

  // ── Uploads ──────────────────────────────────────────────────────

  const patchUpload = useCallback((id: string, patch: Partial<UploadItem>) => {
    setUploads(prev => prev.map(u => (u.id === id ? { ...u, ...patch } : u)));
  }, []);

  const runUpload = useCallback(async (item: UploadItem): Promise<AssetSummary | null> => {
    patchUpload(item.id, { state: 'reading' });
    const probe = await probeFile(item.file);
    patchUpload(item.id, { state: 'uploading' });
    try {
      let asset = await uploadAsset(item.file, {
        kind: probe.kind ?? undefined, width: probe.width, height: probe.height,
        hasAlpha: probe.hasAlpha, durationS: probe.durationS,
      }, fraction => patchUpload(item.id, { progress: fraction }));
      if (probe.thumb) {
        // A failed thumbnail leaves the asset usable, just shown with a placeholder.
        asset = await uploadThumbnail(asset.assetId, probe.thumb).catch(() => asset);
      }
      mergeAssets([asset]);
      patchUpload(item.id, { state: 'done', progress: 1 });
      return asset;
    } catch (err) {
      patchUpload(item.id, { state: 'error', error: err instanceof Error ? err.message : 'Upload failed.' });
      return null;
    }
  }, [mergeAssets, patchUpload]);

  async function addFiles(files: FileList | File[]) {
    const items: UploadItem[] = [...files].map(file => ({ id: crypto.randomUUID(), file, state: 'queued', progress: 0 }));
    setUploads(prev => [...prev.filter(u => u.state !== 'done'), ...items]);
    // A small worker pool, so a 50-file drop doesn't open 50 uploads at once.
    const queue = [...items];
    const uploaded: AssetSummary[] = [];
    const worker = async () => {
      for (let item = queue.shift(); item; item = queue.shift()) {
        const asset = await runUpload(item);
        if (asset) uploaded.push(asset);
      }
    };
    await Promise.all(Array.from({ length: UPLOAD_CONCURRENCY }, worker));

    // One description job for the whole drop. Uploads arrive 'pending' only when the account has AI on.
    const pending = uploaded.filter(a => a.descStatus === 'pending').map(a => a.assetId);
    for (let i = 0; i < pending.length; i += MAX_DESCRIBE_ASSETS) {
      try {
        await describeAssets(pending.slice(i, i + MAX_DESCRIBE_ASSETS));
      } catch (err) {
        // They stay queued; the worker picks up pending assets without a job when it starts.
        setError(`Uploaded, but couldn't queue the descriptions: ${err instanceof Error ? err.message : 'unknown error'}`);
      }
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) void addFiles(e.dataTransfer.files);
  }

  // ── Filtering ────────────────────────────────────────────────────

  const sorted = useMemo(() => [...assets.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt)), [assets]);
  const visible = sorted.filter(a =>
    (kindFilter === 'all' || a.kind === kindFilter)
    && (roleFilter === 'any' || a.roles.includes(roleFilter))
    && matchesSearch(a, search));
  const usedBytes = sorted.reduce((sum, a) => sum + a.sizeBytes, 0);
  const selected = selectedId ? assets.get(selectedId) ?? null : null;
  const quotaMb = author?.storageQuotaMb ?? DEFAULT_STORAGE_QUOTA_MB;

  if (authLoading || !author || !active) return null;

  return (
    <div className="library-page">
      <div className="library-head">
        <div>
          <h1>Library</h1>
          <p className="library-sub">
            Images, music and video for your ads. {formatBytes(usedBytes)} of {formatBytes(quotaMb * 1024 * 1024)} used.
          </p>
        </div>
        <button className="library-upload-btn" onClick={() => fileInput.current?.click()}>Upload files</button>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/bmp,audio/*,video/mp4,video/quicktime,video/webm"
          hidden
          onChange={e => { if (e.target.files) void addFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      <div
        className={`library-dropzone${dragging ? ' library-dropzone-active' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        Drop files here to upload
      </div>

      {uploads.length > 0 && (
        <ul className="library-uploads">
          {uploads.map(u => (
            <li key={u.id} className={`library-upload library-upload-${u.state}`}>
              <span className="library-upload-name">{u.file.name}</span>
              <span className="library-upload-state">
                {u.state === 'queued' && 'Waiting'}
                {u.state === 'reading' && 'Reading'}
                {u.state === 'uploading' && `${Math.round(u.progress * 100)}%`}
                {u.state === 'done' && 'Uploaded'}
                {u.state === 'error' && u.error}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="library-filters">
        <select value={kindFilter} onChange={e => setKindFilter(e.target.value as AssetKind | 'all')} aria-label="Kind">
          <option value="all">All kinds</option>
          <option value="image">Images</option>
          <option value="music">Music</option>
          <option value="video">Video</option>
        </select>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value as ImageRole | 'any')} aria-label="Role">
          <option value="any">Any role</option>
          {IMAGE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input
          type="search"
          placeholder="Search descriptions, tags, filenames"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {error && <p className="page-error-text">{error}</p>}

      {loading ? (
        <p className="library-sub">Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="library-empty">Nothing here yet. Upload backgrounds, covers and music to get started.</p>
      ) : visible.length === 0 ? (
        <p className="library-empty">No assets match those filters.</p>
      ) : (
        <ul className="library-grid">
          {visible.map(a => (
            <li key={a.assetId}>
              <button className="library-tile" onClick={() => setSelectedId(a.assetId)}>
                <span className="library-tile-media">
                  {a.thumbUrl
                    ? <img src={a.thumbUrl} alt="" loading="lazy" />
                    : <span className="library-tile-icon" aria-hidden="true">{a.kind === 'music' ? '♪' : a.kind === 'video' ? '▶' : '▢'}</span>}
                </span>
                <span className="library-tile-name">{a.filename}</span>
                <span className="library-tile-meta">
                  {KIND_LABEL[a.kind]}
                  {a.roles.length > 0 && ` · ${a.roles.join(', ')}`}
                </span>
                <span className={`library-desc-badge library-desc-${a.descStatus}`}>{DESC_LABEL[a.descStatus]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <AssetDetail
          asset={selected}
          aiEnabled={Boolean(author.aiEnabled)}
          aiImagesPerDay={author.aiImagesPerDay}
          onClose={() => setSelectedId(null)}
          onSaved={updated => mergeAssets([updated])}
          onDeleted={assetId => {
            setSelectedId(null);
            setAssets(prev => { const next = new Map(prev); next.delete(assetId); return next; });
          }}
        />
      )}
    </div>
  );
}

function AssetDetail({ asset, aiEnabled, aiImagesPerDay, onClose, onSaved, onDeleted }: {
  asset:     AssetSummary;
  aiEnabled: boolean;
  /** For the confirmation; undefined if the session predates the field. */
  aiImagesPerDay?: number;
  onClose:   () => void;
  onSaved:   (updated: AssetSummary) => void;
  onDeleted: (assetId: string) => void;
}) {
  const [roles, setRoles]         = useState<ImageRole[]>(asset.roles);
  const [tagsText, setTagsText]   = useState(asset.tags.join(', '));
  const [bookCode, setBookCode]   = useState(asset.bookCode ?? '');
  const [descShort, setDescShort] = useState(asset.descShort ?? '');
  const [descLong, setDescLong]   = useState(asset.descLong ?? '');
  const [mood, setMood]           = useState(asset.mood ?? '');
  const [busy, setBusy]           = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  /** Set after "Describe with AI" succeeds, so the panel says it worked, not only the badge. */
  const [justQueued, setJustQueued] = useState(false);
  /**
   * Set on the first keystroke; while true, updates to this asset arriving
   * from polling don't overwrite that part of the form. Kept apart, so a
   * description arriving from Claude still fills in the (read-only)
   * description fields while roles or tags have unsaved edits.
   */
  const [metaDirty, setMetaDirty] = useState(false);
  const [descDirty, setDescDirty] = useState(false);
  const dirty = metaDirty || descDirty;
  /** Claude is on it: the description fields are read-only until it's done or the user takes it out of the queue. */
  const locked = isDescQueued(asset.descStatus);

  // Re-seed the form when a different asset is opened, or when this one is
  // updated underneath (e.g. its description arrives while the panel is
  // open) — but never over unsaved edits.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const otherAsset = seededFor.current !== asset.assetId;
    seededFor.current = asset.assetId;
    if (otherAsset || !metaDirty) {
      setRoles(asset.roles);
      setTagsText(asset.tags.join(', '));
      setBookCode(asset.bookCode ?? '');
      setMetaDirty(false);
    }
    if (otherAsset || !descDirty) {
      setDescShort(asset.descShort ?? '');
      setDescLong(asset.descLong ?? '');
      setMood(asset.mood ?? '');
      setDescDirty(false);
    }
    if (otherAsset) {
      setConfirmDelete(false);
      setConfirmReplace(false);
      setJustQueued(false);
      setError(null);
    }
  }, [asset, metaDirty, descDirty]);

  // Escape closes only while nothing is unsaved, so a stray key press can't throw away typing
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !dirty) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, dirty]);

  async function save() {
    setBusy(true);
    setError(null);
    const tags = tagsText.split(',').map(t => t.trim()).filter(Boolean);
    // Only send description fields the user typed in and actually changed,
    // so saving tags doesn't claim an AI description as the user's own (or
    // trip the lock while it's queued).
    const update: Parameters<typeof updateAsset>[1] = { tags, bookCode: bookCode.trim() || null };
    if (asset.kind === 'image') update.roles = roles;
    if (descDirty && !locked) {
      if (descShort.trim() !== (asset.descShort ?? '')) update.descShort = descShort.trim() || null;
      if (descLong.trim() !== (asset.descLong ?? ''))   update.descLong = descLong.trim() || null;
      if (mood.trim() !== (asset.mood ?? ''))           update.mood = mood.trim() || null;
    }
    try {
      const saved = await updateAsset(asset.assetId, update);
      setMetaDirty(false);
      setDescDirty(false);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  /** "Write it myself": out of the AI queue, fields unlocked. If Claude got there first, its description simply shows. */
  async function writeOwn() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await cancelDescription(asset.assetId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn\'t take it out of the queue.');
    } finally {
      setBusy(false);
    }
  }

  /** Anything the AI would overwrite, whoever wrote it. */
  const hasDescription = Boolean(asset.descShort || asset.descLong || asset.mood);

  async function describeWithAi() {
    // Replacing a description (the user's or an earlier AI one) loses it and uses up a daily AI description,
    // so that takes a second click. A first description loses nothing, so it's one click.
    if (hasDescription && !confirmReplace) {
      setConfirmReplace(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await describeAssets([asset.assetId]);
      // The job doesn't return the asset; show it queued now (which also restarts polling) until the next poll
      onSaved({ ...asset, descStatus: 'pending', descBy: null, descError: null, updatedAt: new Date().toISOString() });
      setConfirmReplace(false);
      setJustQueued(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn\'t queue the description.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await deleteAsset(asset.assetId);
      onDeleted(asset.assetId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
      setBusy(false);
    }
  }

  const facts = [
    KIND_LABEL[asset.kind],
    asset.width && asset.height ? `${asset.width}×${asset.height}` : null,
    formatDuration(asset.durationS),
    asset.hasAlpha ? 'transparent' : null,
    formatBytes(asset.sizeBytes),
  ].filter(Boolean).join(' · ');

  return (
    // No click-outside-to-close, like AuthModal: a stray click mid-edit would lose what was typed. The × closes it.
    <div className="library-detail-backdrop">
      <aside className="library-detail" role="dialog" aria-label={asset.filename}>
        <div className="library-detail-head">
          <h2>{asset.filename}</h2>
          <button className="library-detail-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <p className="library-sub">{facts}</p>

        <div className="library-detail-preview">
          {asset.kind === 'image' && <img src={asset.fileUrl} alt={asset.descShort ?? asset.filename} />}
          {asset.kind === 'music' && <audio src={asset.fileUrl} controls preload="none" />}
          {asset.kind === 'video' && <video src={asset.fileUrl} controls preload="metadata" poster={asset.thumbUrl ?? undefined} />}
        </div>

        {/* Grouped by who fills it in: your filing (never locked or overwritten), then everything the AI
            writes or locks, so it's clear what "Redo with AI" replaces. */}
        <section className="library-section" aria-label="Your details">
          <div className="library-form">
            <div className="library-field-row">
              <label className="library-field">
                Book
                <BookSelect value={bookCode} onChange={code => { setBookCode(code); setMetaDirty(true); }} />
              </label>
              <label className="library-field library-field-grow" onInput={() => setMetaDirty(true)}>
                Tags (comma-separated)
                <input type="text" value={tagsText} onChange={e => setTagsText(e.target.value)} placeholder="castle, night, series name" />
              </label>
            </div>
          </div>
          {asset.kind === 'image' && (
            <fieldset className="library-roles" onChange={() => setMetaDirty(true)}>
              <legend>Use as</legend>
              {IMAGE_ROLES.map(r => (
                <label key={r}>
                  <input
                    type="checkbox"
                    checked={roles.includes(r)}
                    onChange={e => setRoles(prev => e.target.checked ? [...prev, r] : prev.filter(x => x !== r))}
                  />
                  {r}
                </label>
              ))}
              {/* The AI only suggests roles for an image with none ticked; it never changes the user's choice */}
              {aiEnabled && roles.length === 0 && <p className="library-desc-note">Leave empty and the AI will suggest some.</p>}
            </fieldset>
          )}
        </section>

        <section className="library-section" aria-label="Description">
          {/* The description's status and the AI action that changes it, side by side */}
          <div className="library-desc-head">
            <h3 className="library-section-title">Description</h3>
            <p className={`library-desc-badge library-desc-${asset.descStatus}`}>
              {DESC_LABEL[asset.descStatus]}{asset.descBy === 'user' && ' (edited by you)'}
            </p>
            {aiEnabled && isDescribable(asset.kind) && !locked && (
              <button className="library-ai-btn" disabled={busy || descDirty || confirmReplace} onClick={describeWithAi}
                title={descDirty ? 'Save or undo your edits first' : hasDescription ? 'Replace the description with a new one from AI (asks first)'
                  : 'Have AI write the description, mood and roles'}>
                {asset.descStatus === 'failed' ? 'Try again with AI' : hasDescription ? 'Redo with AI…' : 'Describe with AI'}
              </button>
            )}
          </div>
          {asset.descError && <p className="page-error-text">{asset.descError}</p>}

          {confirmReplace && !locked && (
            <div className="library-confirm" role="alertdialog" aria-label="Replace the description?">
              <p>
                Replace {asset.descBy === 'user' ? 'your description' : 'the current description'} and mood with a new one from AI?
                {aiImagesPerDay !== undefined && ` This uses 1 of your ${aiImagesPerDay} AI descriptions a day.`}
              </p>
              <div className="library-detail-actions">
                <button className="library-save" disabled={busy} onClick={describeWithAi}>Replace</button>
                <button disabled={busy} onClick={() => setConfirmReplace(false)}>Cancel</button>
              </div>
            </div>
          )}

          {locked ? (
            <p className={`library-desc-note${justQueued ? ' library-desc-queued' : ''}`}>
              {justQueued
                ? 'Queued. The description will fill in here when the AI is done; you can close this and keep working.'
                : `The AI will describe this ${asset.kind === 'video' ? 'video' : 'image'}; the description fields fill in when it's done.`}{' '}
              <button type="button" className="library-link-btn" disabled={busy} onClick={writeOwn}>Write it myself instead</button>
            </p>
          ) : isDescribable(asset.kind) && !aiEnabled && (
            <p className="library-desc-note">AI descriptions are turned off for your account. An admin can turn them on.</p>
          )}

          <div className="library-form" onInput={() => { if (!locked) setDescDirty(true); }}>
            <label className="library-field">
              Short description
              <textarea rows={2} maxLength={300} value={descShort} readOnly={locked} onChange={e => setDescShort(e.target.value)}
                placeholder={locked ? '' : 'e.g. Lone rider on a ridge above a burning village at dusk, small figure lower third'} />
            </label>
            <label className="library-field">
              Long description
              <textarea rows={4} maxLength={2000} value={descLong} readOnly={locked} onChange={e => setDescLong(e.target.value)} />
            </label>
            <label className="library-field">
              Mood
              <input type="text" maxLength={60} value={mood} readOnly={locked} onChange={e => setMood(e.target.value)}
                placeholder={locked ? '' : 'e.g. ominous, epic'} />
            </label>
          </div>

          {/* Measured by the worker (no AI), read-only */}
          {!asset.colors && asset.measureStatus === 'pending' && (
            <p className="library-desc-note">Colours and text placement are measured shortly after upload.</p>
          )}
          {asset.colors && (
            <div className="library-colors">
              <span className="library-swatches" aria-hidden="true">
                {asset.colors.dominant.map(d => (
                  <span key={d.hex} className="library-swatch" style={{ background: d.hex, flexGrow: Math.max(d.share, 0.04) }}
                    title={`${d.hex} ${Math.round(d.share * 100)}%`} />
                ))}
              </span>
              <span className="library-sub">
                {describeColors(asset.colors)}
                {asset.textSafeZones.length > 0 && ` · text fits ${asset.textSafeZones.join(', ')}`}
                {asset.suggestedZoomTarget && ` · zoom ${asset.suggestedZoomTarget}`}
              </span>
            </div>
          )}
        </section>

        {error && <p className="page-error-text">{error}</p>}

        <div className="library-detail-actions">
          <button className="library-save" disabled={busy} onClick={save}>Save</button>
          {confirmDelete ? (
            <>
              <button className="library-delete" disabled={busy} onClick={remove}>Delete permanently</button>
              <button disabled={busy} onClick={() => setConfirmDelete(false)}>Cancel</button>
            </>
          ) : (
            <button className="library-delete-link" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete…</button>
          )}
        </div>
      </aside>
    </div>
  );
}
